import { FC, useState } from "react";

import isPropValid from "@emotion/is-prop-valid";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  lighten,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment-timezone";
import ScheduleSelector from "react-schedule-selector";
import { StyleSheetManager } from "styled-components";

import { PROVIDER_REGISTRATION_TITLE_PROPS } from "~/utils/providerRegistrationHelpers";
import { DETAIL_FIELDS } from "./tableHelpers";

interface TableDetailRowProps {
  params: any;
  expandedIds: Set<any>;
  setAnimatingIds: (_prev: any) => void;
}

const TableDetailRow: FC<TableDetailRowProps> = ({ params, expandedIds, setAnimatingIds }) => {
  const theme = useTheme();

  const mId = params.row.master.registration.providerRegistrationId;
  const isOpen = expandedIds.has(mId);

  const [showSchedule, setShowSchedule] = useState(false);

  const handleCloseSchedule = () => setShowSchedule(false);

  return (
    <Box sx={{ width: "100%" }}>
      <Collapse
        in={isOpen}
        onExited={() => {
          setAnimatingIds((prev: any) => {
            const next = new Set(prev);
            next.delete(mId);
            return next;
          });
        }}
        timeout="auto"
      >
        <Box sx={{ p: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Box overflow="auto" p={1}>
                {DETAIL_FIELDS.map(({ key, label }, index) => {
                  let value = params.row.master.registration[key];

                  if (["professionalCredentials", "considerations"].includes(key)) {
                    value = value.map(({ value }: { value: string }) => value).join(", ");
                  } else if (key === "genderPreference") {
                    value = value.value;
                  } else if (typeof value === "boolean") {
                    value = value ? "Yes" : "No";
                  }

                  return (
                    <Box
                      key={key}
                      alignItems="center"
                      display="flex"
                      gap={1}
                      justifyContent="space-between"
                      p={0.5}
                      sx={{
                        backgroundColor: index % 2 !== 0 ? "action.focus" : "transparent",
                        borderRadius: 1,

                        "& > :nth-of-type(2)": {
                          textAlign: "right",
                        },
                      }}
                    >
                      <Typography>{label}:</Typography>
                      {key === "generalAvailability" && (
                        <>
                          <Button onClick={() => setShowSchedule(true)} size="small">
                            View
                          </Button>
                          <Dialog maxWidth="md" onClose={handleCloseSchedule} open={showSchedule} scroll="paper" fullWidth>
                            <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography {...PROVIDER_REGISTRATION_TITLE_PROPS}>{params.row.master.name} Availability</Typography>
                            </DialogTitle>
                            <DialogContent dividers>
                              <Box
                                sx={{
                                  pointerEvents: "none",

                                  "& > div:first-of-type > div:first-of-type > div:first-of-type": {
                                    position: "sticky",
                                    top: theme.spacing(-2),
                                    backgroundColor: "background.paper",
                                  },
                                }}
                              >
                                <StyleSheetManager shouldForwardProp={(prop) => isPropValid(prop)}>
                                  <ScheduleSelector
                                    hourlyChunks={2}
                                    hoveredColor={lighten(theme.palette.primary.main, 0.4)}
                                    maxTime={24}
                                    minTime={0}
                                    numDays={7}
                                    onChange={() => {}}
                                    renderDateLabel={(date) => (
                                      <Typography
                                        position="sticky"
                                        sx={{
                                          backgroundColor: "background.paper",
                                        }}
                                        textAlign="center"
                                        top={theme.spacing(-2)}
                                        variant="h6"
                                      >
                                        {moment(date).format("dddd")}
                                      </Typography>
                                    )}
                                    renderTimeLabel={(time) => <Typography textAlign="end">{moment(time).format("h:mm a")}</Typography>}
                                    selectedColor={theme.palette.primary.main}
                                    selection={value}
                                    startDate={new Date(1769990400000)} // schedule will show Sunday-Saturday
                                    timeFormat="h:mma"
                                    unselectedColor={lighten(theme.palette.primary.light, 0.5)}
                                  />
                                </StyleSheetManager>
                              </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 2 }}>
                              <Button onClick={handleCloseSchedule} variant="contained">
                                Close
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                      {key !== "generalAvailability" && <Typography sx={{ textTransform: "capitalize" }}>{value}</Typography>}
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TableDetailRow;
